import { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Stack,
  Button,
  Paper,
  useTheme,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import NavigationIcon from "@mui/icons-material/Navigation";
import { pageStyles } from "../../Styles/pageStyles";
import PlaceOrderDrawer from "./PlaceOrderDrawer";
import OrderConfirmedDrawer from "./OrderConfirmedDrawer";
import { Nodiv } from "./emptyDiv";
import {
  currencyFormatter,
  apiHandler,
  widthData,
  LoaderFun,
  ErrLoaderOff,
  isOpenNow
} from "@shared/Functions/apiHandler";
import { commonServices } from "../../Services/commonServices";
import { MapPin } from "lucide-react";
import MiniLoader from "@shared/Components/miniLoader";
import { useDrawerOpenFun } from "../../Context/modelContext";
import { toast } from "sonner";
import { useCheckLogin } from "../../utils/functions";

export default function StationDetailDrawer({ open, vendorId, onClose }) {
  const theme = useTheme();
  const classes = pageStyles(theme);
  const [openOrder, setOpenOrder] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmedOrder] = useState(null);
  const [vendorDetails, setVendorDetails] = useState({});
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [loader, setLoader] = useState(false);
  const [serverTime, setServerTime] = useState("");
  const { location } = useDrawerOpenFun();
  // custom hook for check login
  const checkLogin = useCheckLogin();

  const getVendorDetails = async (vendorId) => {
    setLoader(true);
    if (!location.lat && !location.lng) {
      toast.error("Please enable location");
      return false;
    }
    const res = await commonServices.vendorInfo({ id: vendorId, lat: location.lat, lng: location.lng });
    LoaderFun(res, setLoader);
    ErrLoaderOff(res, setLoader);
    if (res?.status === 200) {
      setVendorDetails(res?.data?.data);
      setServerTime(res.data.data.serverTimeUtc);
    }
  };

  useEffect(() => {
    if (!vendorId) return;
    getVendorDetails(vendorId);
  }, [vendorId]);

  const mtoKm = (distanceMeters) => {
    return (distanceMeters / 1000).toFixed(0);
  };

  const selectProductsFunc = (data) => {
    console.log(data.productType, "productType");
    if (data?.stock < 1 && data.productType === 1) {
      alert("Out of stock");
      return;
    }

    setSelectedProduct((prev) => {
      const exists = prev.some((item) => item.productId === data.productId);

      if (exists) {
        // remove the existing productId (toggle off)
        return prev.filter((item) => item.productId !== data.productId);
      }

      // otherwise add it (toggle on)
      return [...prev, { productId: data.productId }];
    });
  };

  const addFav = async () => {
    let payload = {
      type: 1, // 1-Vendor 2-Product
      vendorId: vendorId, // When type is 1
    };
    const res = await commonServices.addFav(payload);
    apiHandler(res, classes.successToast, true);
    if (res.status === 200) {
      getVendorDetails(vendorId);
    }
  };

  const removeFav = async () => {
    let payload = {
      type: 1, // 1-Vendor 2-Product
      vendorId: 1, // When type is 1
    };
    const res = await commonServices.removeFav(payload);
    apiHandler(res, classes.successToast, true);
    if (res?.status === 200) {
      getVendorDetails(vendorId);
    }
  };

  const clearData = () => {
    setSelectedProduct([]);
    onClose();
  };

  const openStatus = isOpenNow(
    vendorDetails?.stationDetails?.openTime,
    vendorDetails?.stationDetails?.closeTime,
    serverTime
  );

  return (
    <>
      {/* Station detail drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={clearData}
        slotProps={{
          paper: {
            sx: widthData,
          },
        }}
      >
        {loader ? (
          <MiniLoader />
        ) : (
          <>
            {/* Top image */}
            <Box sx={classes.DrawerImgbg}>
              <Box
                component="img"
                src={vendorDetails?.stationDetails?.imageUrl}
                alt={vendorDetails?.stationDetails?.name}
                sx={classes.DrawerImg}
              />
              <IconButton
                size="small"
                onClick={clearData}
                sx={classes.closeBtn}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={classes.DrawerContent}>
              {/* Header */}
              <Box sx={classes.flex_center_btn}>
                <Box>
                  <Typography sx={classes.darkTextsmd500} mb={1}>
                    {vendorDetails?.stationDetails?.name}
                  </Typography>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={classes.flex_c}
                    >
                      <MapPin size={18} />
                      <Typography sx={classes.lightTextSmall}>
                        {mtoKm(vendorDetails?.stationDetails?.distanceMeters)}
                        km away
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      variant="outlined"
                      label={openStatus ? "Open" : "Closed"}
                      color={openStatus ? "success" : "error"}
                      sx={openStatus ? classes.greenChip : undefined}
                    />
                  </Stack>
                </Box>

                <Box sx={{ textAlign: "right" }}>
                  <IconButton
                    size="small"
                    color="primary"
                    sx={classes.favIcon}
                    onClick={() => {
                      vendorDetails?.isFav ? checkLogin(() => { removeFav() }) : checkLogin(() => { addFav() });
                    }}
                  >

                    {vendorDetails?.isFav ? (
                      <FavoriteIcon fontSize="small" />
                    ) : (
                      <FavoriteBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Box>

              {/* Address card */}
              <Paper elevation={0} sx={classes.addressDiv}>
                <Box>
                  <Typography>Address</Typography>
                  <Typography sx={classes.lightTextsmd}>
                    {vendorDetails?.stationDetails?.address ||
                      "No address provided"}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={
                    <NavigationIcon
                      fontSize="small"
                      sx={{ transform: "rotate(45deg)" }}
                    />
                  }
                  size="small"
                  onClick={() => {
                    if (vendorDetails?.stationDetails?.address) {
                      const q = encodeURIComponent(
                        vendorDetails?.stationDetails?.address
                      );
                      window.open(
                        `https://www.google.com/maps/search/?api=1&query=${q}`,
                        "_blank"
                      );
                    }
                  }}
                >
                  Navigate
                </Button>
              </Paper>

              {/* Current Prices */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={classes.darkTextMedium500}>Products</Typography>

                {vendorDetails?.vendorProducts?.length === 0 && (
                  <Nodiv title="No fuel types available, Check back later" />
                )}

                <Stack spacing={1} mt={1}>
                  {vendorDetails?.vendorProducts?.map((f) => (
                    <Paper
                      key={f.type}
                      elevation={0}
                      sx={
                        selectedProduct.find(
                          (item) => item.productId === f.productId
                        )
                          ? classes.priceDivActive
                          : f.stock < 10 ? classes.priceNoData : classes.priceDiv
                      }
                      onClick={() => {
                        checkLogin(() => {selectProductsFunc(f)});
                      }}
                    >
                      <Box sx={{ ...classes.flex_c }}>
                        <Box sx={classes.fuelIcon}>
                          <img
                            src={f.imageUrl}
                            style={classes.serviceIcon}
                            alt={f.name}
                          />
                        </Box>
                        <Box>
                          <Typography sx={classes.darkTextMedium}>
                            {f.name}
                          </Typography>

                          {f?.stock < 10 && (
                            <Typography sx={classes.errorText2}>
                              Low Stock - {f.stock} {f.measurementName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography sx={classes.darkTextMedium}>
                        {currencyFormatter()}
                        {f.price}/{f.measurementName}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>

              <Box>
                {vendorDetails?.vendorServices?.length != 0 && (
                  <Typography sx={{ ...classes.darkTextMedium500, mt: 2 }}>
                    Services
                  </Typography>
                )}

                <Stack spacing={1} mt={1}>
                  {vendorDetails?.vendorServices?.map((f) => (
                    <Paper
                      key={f.type}
                      elevation={0}
                      sx={
                        selectedProduct.find(
                          (item) => item.productId === f.productId
                        )
                          ? classes.priceDivActive
                          : classes.priceDiv
                      }
                      // onClick={() => { selectProduct(f) }}
                      onClick={() => {
                        selectProductsFunc(f);
                      }}
                    >
                      <Box sx={{ ...classes.flex_c }}>
                        <Box sx={classes.fuelIcon}>
                          <img
                            src={f.imageUrl}
                            style={classes.serviceIcon}
                            alt={f.name}
                          />
                        </Box>
                        <Box>
                          <Typography sx={classes.darkTextMedium}>
                            {f.name}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={classes.darkTextMedium}>
                        {currencyFormatter()}
                        {f.price}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>

              {openStatus &&
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={selectedProduct.length === 0}
                  onClick={() => {
                    setOpenOrder(true);
                  }}
                >
                  Place Order{" "}
                  {selectedProduct.length > 0 && `(${selectedProduct.length})`}
                </Button>}


              {!openStatus &&
                <Button
                  variant="outlined"
                  fullWidth
                  color="error"
                  sx={{ mt: 2 }}

                >
                  Station Closed, Check back later
                </Button>}
            </Box>
          </>
        )}
      </Drawer>

      <PlaceOrderDrawer
        open={openOrder}
        stationId={vendorDetails?.stationDetails?.vendorId}
        onClose={() => setOpenOrder(false)}
        selectedProducts={selectedProduct}
      />

      {/* Order Confirmed Drawer */}
      <OrderConfirmedDrawer
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        order={confirmedOrder}
      />
    </>
  );
}
